import axiosInstance from '../../../config/axios';

interface UnlockResponse {
  ticket: string;
  ttl: number;
  expires_at: string;
}

const STORAGE_KEY = 'compensation_unlock';

class CompensationUnlockService {
  async unlock(pin: string): Promise<UnlockResponse> {
    const response = await axiosInstance.post<UnlockResponse>('/api/compensation-unlock/', { pin });

    if (response.data.ticket) {
      this.storeTicket(response.data.ticket, response.data.ttl);
    }

    return response.data;
  }

  storeTicket(ticket: string, ttl: number): void {
    const expiresAt = Date.now() + (ttl * 1000);
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ ticket, expiresAt }));
  }

  getTicket(): string | null {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    try {
      const { ticket, expiresAt } = JSON.parse(stored);

      if (Date.now() >= expiresAt) {
        this.clearTicket();
        return null;
      }

      return ticket;
    } catch {
      this.clearTicket();
      return null;
    }
  }

  getTimeRemaining(): number {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (!stored) return 0;

    try {
      const { expiresAt } = JSON.parse(stored);
      const remaining = Math.max(0, Math.floor((expiresAt - Date.now()) / 1000));
      return remaining;
    } catch {
      return 0;
    }
  }

  isUnlocked(): boolean {
    return this.getTicket() !== null;
  }

  clearTicket(): void {
    sessionStorage.removeItem(STORAGE_KEY);
  }

  async setPin(employeeId: number, pin: string): Promise<void> {
    await axiosInstance.post(`/api/employees/${employeeId}/compensation-pin/`, { pin });
  }

  async rotatePin(employeeId: number, newPin: string): Promise<void> {
    await axiosInstance.put(`/api/employees/${employeeId}/compensation-pin/`, { pin: newPin });
  }

  async disablePin(employeeId: number): Promise<void> {
    await axiosInstance.delete(`/api/employees/${employeeId}/compensation-pin/`);
  }
}

export default new CompensationUnlockService();
